import { useState, useMemo } from 'react';
import type { TableConfig, FormatType } from '../../types/viewspec';

interface DataTableProps {
  title?: string;
  config: TableConfig;
  data?: Record<string, unknown>[];
}

function formatValue(value: unknown, format?: FormatType): string {
  if (value === null || value === undefined) return '-';

  switch (format) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    case 'currency':
      return typeof value === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
        : String(value);
    case 'percent':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : String(value);
    case 'date':
      return value instanceof Date
        ? value.toLocaleDateString()
        : typeof value === 'string' || typeof value === 'number'
        ? new Date(value).toLocaleDateString()
        : String(value);
    case 'datetime':
      return value instanceof Date
        ? value.toLocaleString()
        : typeof value === 'string' || typeof value === 'number'
        ? new Date(value).toLocaleString()
        : String(value);
    default:
      return String(value);
  }
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

export default function DataTable({ title, config, data = [] }: DataTableProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const pageSize = config.pageSize || 10;

  // Filter data by search query
  const filteredData = useMemo(() => {
    if (!searchQuery || !config.searchable) return data;

    const query = searchQuery.toLowerCase();
    return data.filter((row) =>
      config.columns.some((col) => {
        const value = getNestedValue(row, col.key);
        return String(value).toLowerCase().includes(query);
      })
    );
  }, [data, searchQuery, config.searchable, config.columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig || !config.sortable) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getNestedValue(a, sortConfig.key);
      const bVal = getNestedValue(b, sortConfig.key);

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortConfig, config.sortable]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!config.paginated) return sortedData;
    const start = currentPage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, config.paginated]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key: string) => {
    if (!config.sortable) return;

    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.direction === 'asc'
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <div className="bg-grey-light h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-grey-medium/20 flex items-center justify-between">
        {title && (
          <h3 className="text-h3 font-light text-black">
            {title}
          </h3>
        )}
        {config.searchable && (
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(0);
            }}
            className="px-3 py-2 bg-white border border-grey-medium/30 focus:outline-none focus:border-black text-black font-light text-sm max-w-xs"
          />
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-white sticky top-0">
            <tr>
              {config.columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                  className={`px-4 py-3 text-left font-mono text-xs text-grey-medium uppercase tracking-wider ${
                    col.sortable !== false && config.sortable
                      ? 'cursor-pointer hover:bg-grey-light transition-colors duration-150'
                      : ''
                  } ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}`}
                  style={{ width: col.width }}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {config.sortable && sortConfig?.key === col.key && (
                      <span className="text-black">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-medium/20">
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={config.columns.length}
                  className="px-4 py-8 text-center text-grey-medium font-light"
                >
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-white transition-colors duration-150"
                >
                  {config.columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-3 text-sm text-black font-light ${
                        col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''
                      }`}
                    >
                      {formatValue(getNestedValue(row, col.key), col.format)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {config.paginated && totalPages > 1 && (
        <div className="p-4 border-t border-grey-medium/20 flex items-center justify-between">
          <div className="font-mono text-xs text-grey-medium uppercase">
            Showing {currentPage * pageSize + 1} to{' '}
            {Math.min((currentPage + 1) * pageSize, sortedData.length)} of{' '}
            {sortedData.length} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="py-2 px-4 bg-black text-white font-mono text-xs uppercase tracking-wider rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="py-2 px-4 bg-black text-white font-mono text-xs uppercase tracking-wider rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
